import React from 'react';
import errorPageConfig from '../config/errorPageConfig';

/**
 * Universal Error Page Component
 * Displayed when application encounters issues on any device or browser
 */
const ErrorPage = ({ errorInfo }) => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-purple-50 to-indigo-50 p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        {/* Top brand area - Using gradient background for visual hierarchy */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 text-center">
          <h1 className="text-2xl font-bold text-white">{errorPageConfig.title}</h1>
          <div className="h-1 w-16 bg-white opacity-30 mx-auto mt-2 rounded-full"></div>
        </div>
        
        {/* Error message area - Centered alignment and improved styling */}
        <div className="px-8 py-6 bg-white">
          <div className="flex flex-col items-center justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4 border-2 border-red-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={errorPageConfig.errorIcon} />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 text-center">{errorPageConfig.errorTitle}</h2>
          </div>
          
          <p className="text-gray-700 mb-6 text-center">
            {errorPageConfig.errorMessage}
          </p>
          
          <p className="text-sm font-medium text-gray-600 mb-2 text-center">
            {errorPageConfig.troubleshootingText}
          </p>
        </div>
        
        {/* Solutions area - All items non-clickable for consistency */}
        <div className="px-6 pb-6 bg-white">
          <div className="space-y-3">
            {errorPageConfig.solutions.map((solution) => (
              <div key={solution.id} className="solution-item transition-all duration-200 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${solution.gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <div className="text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={solution.icon} />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 flex-grow">
                    <h3 className="font-medium text-gray-800">{solution.title}</h3>
                    <p className="text-sm text-gray-600">
                      {solution.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer support info - Simplified design */}
        <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-indigo-50 border-t border-gray-200">
          <div className="text-center">
            <a 
              href={`mailto:${errorPageConfig.supportEmail}`} 
              className="inline-flex items-center text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={errorPageConfig.emailIcon} />
              </svg>
              {errorPageConfig.supportText}: {errorPageConfig.supportEmail}
            </a>
          </div>
        </div>
      </div>
      
      {/* Brand watermark */}
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500">
          © {new Date().getFullYear()} {errorPageConfig.title} • {errorPageConfig.copyright}
        </p>
        {errorInfo && process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-white border border-red-200 rounded-md max-w-xl mx-auto text-left shadow-sm">
            <p className="text-xs font-medium text-red-700 mb-1">Error details (visible in development only):</p>
            <pre className="text-xs text-red-600 overflow-auto max-h-40 p-2 bg-red-50 rounded">
              {JSON.stringify(errorInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorPage; 